import clsx from "clsx";
import React from "react";
import {
  MenuItem,
  MenuItemProps,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Check as CheckIcon } from "@mui/icons-material";

import { CardAvatar } from "../card/CardAvatar";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useSwitchUser } from "../../hooks/useSwitchUser";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import { TeamRole, UserRole } from "../../constants";
import { UserAccountInfoDto } from "@growth-machine-llc/stridist-api-client";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 270,
  },

  divider: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.selected.main,
  },

  coachAvatarWrapper: {
    position: "relative",
    width: 48,
    height: 48,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },

  coachAvatar: {
    width: "100%",
    height: "100%",
  },
  workspaceAvatarWrapper: {
    position: "absolute",
    top: -8,
    right: -12,
    width: 26,
    height: 26,
    borderRadius: "50%",
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
  },

  workspaceAvatar: {
    width: "100%",
    height: "100%",
  },

  displayName: {
    color: theme.palette.common.black,
    fontWeight: "bold",
    fontSize: 14,
  },

  info: {
    color: theme.palette.avatar,
    fontSize: 13,
  },

  currentIcon: {
    color: theme.palette.secondary.main,
    height: 22,
  },
}));

export interface UserAccountMenuItemProps
  extends Omit<MenuItemProps, "button" | "children" | "onClose"> {
  user: UserAccountInfoDto;
  onClose: (event: React.MouseEvent) => void;
}

export const UserAccountMenuItem = React.forwardRef<
  HTMLLIElement,
  UserAccountMenuItemProps
>(function (props: UserAccountMenuItemProps, ref) {
  const { className, user, onClose, ...other } = props;
  const s = useStyles();
  const me = useCurrentUser();
  const userId = user.id;
  const isCurrent = me?.id === userId;
  const workspace = useCurrentWorkspace();
  const switchUser = useSwitchUser();

  const isTeamAccount = Boolean(me?.isTeamAccount);
  const isCoach = user.role === UserRole.COACH;

  const isOwnerCoach =
    isTeamAccount && isCoach && user.teamRole === TeamRole.OWNER;

  const isInvitedCoach =
    isTeamAccount && isCoach && user.teamRole === TeamRole.COACH;

  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      onClose(event);
      if (!isCurrent) {
        switchUser(userId, user.role);
      }
    },
    [onClose, switchUser, userId, user.role, isCurrent],
  );

  return (
    <div>
      <MenuItem
        onClick={handleClick}
        className={clsx(s.root, className)}
        ref={ref}
        dense
        {...other}
      >
        <Box className={s.coachAvatarWrapper}>
          <CardAvatar src={user.photoUrl} className={s.coachAvatar} />
          {isInvitedCoach && workspace?.logoUrl && (
            <Box className={s.workspaceAvatarWrapper}>
              <CardAvatar
                src={workspace.logoUrl}
                className={s.workspaceAvatar}
              />
            </Box>
          )}
        </Box>
        <ListItemText
          classes={{
            primary: s.displayName,
            secondary: s.info,
          }}
          primary={user.displayName}
          secondary={
            isOwnerCoach
              ? "Main coach"
              : user.role === UserRole.COACH
                ? "Coach"
                : user.isSample
                  ? "Sample Client"
                  : "Client"
          }
        />
        {isCurrent && <CheckIcon className={s.currentIcon} />}
      </MenuItem>
      <Divider className={s.divider} />
    </div>
  );
});
